import { AlertCircleIcon } from 'lucide-react';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { SplitMethod, formatAmountForCurrency } from '@splitfiesta/shared';

import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';

import { SplitCalculationResult } from './types';

interface SplitSummaryProps {
  splitMethod: SplitMethod;
  currency: string;
  numericAmount: number;
  calculationResult: SplitCalculationResult;
}

export function SplitSummary({
  splitMethod,
  currency,
  numericAmount,
  calculationResult,
}: SplitSummaryProps) {
  const { t } = useTranslation();
  const {
    totalAllocated,
    remaining,
    isFullyAllocated,
    totalPercentage,
    totalShares,
  } = calculationResult;

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <div
        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg ${
          isFullyAllocated
            ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300'
            : remaining > 0
              ? 'bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300'
              : 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300'
        }`}
      >
        {/* Status message - full width on mobile */}
        <div className="flex items-center gap-2">
          {isFullyAllocated ? (
            <CheckmarkIcon className="w-5 h-5 shrink-0" />
          ) : (
            <AlertCircleIcon className="w-5 h-5 shrink-0" />
          )}
          <span className="text-sm font-semibold">
            {isFullyAllocated
              ? t('expenses:messages.fullyAllocated')
              : remaining > 0
                ? t('expenses:labels.remaining')
                : t('expenses:labels.over')}
          </span>
        </div>

        {/* Amount details */}
        <div className="text-sm pl-7 sm:pl-0 sm:text-right">
          {splitMethod === 'percentage' ? (
            <div className="flex items-center gap-2 sm:justify-end">
              <span className="font-medium">
                {t('expenses:labels.total')}: {totalPercentage.toFixed(0)}%
              </span>
              {!isFullyAllocated && (
                <span className="text-xs opacity-75">
                  ({Math.abs(100 - totalPercentage).toFixed(0)}%{' '}
                  {remaining > 0
                    ? t('expenses:labels.remaining')
                    : t('expenses:labels.over')}
                  )
                </span>
              )}
            </div>
          ) : splitMethod === 'shares' ? (
            <div className="flex flex-col gap-1 sm:items-end">
              <div className="flex items-center gap-2 sm:justify-end">
                <span className="font-medium">
                  {totalShares} {t('expenses:labels.shares')}
                </span>
                <span className="text-xs opacity-75">
                  = {formatAmountForCurrency(totalAllocated, currency)}
                </span>
              </div>
              {totalShares > 0 && (
                <span className="text-xs opacity-75">
                  {t('expenses:labels.oneShareValue', {
                    value: formatAmountForCurrency(
                      numericAmount / totalShares,
                      currency
                    ),
                  })}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:justify-end">
              <span className="font-medium">
                {formatAmountForCurrency(totalAllocated, currency)}
              </span>
              {!isFullyAllocated && (
                <span className="text-xs opacity-75">
                  / {formatAmountForCurrency(numericAmount, currency)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
